import React, { useState, useEffect, useRef, useMemo } from 'react';
import MapPanel from "../basic/MapPanel";
import MapLegend from '../specific/MapLegend';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { BlurCircularOutlined } from '@mui/icons-material';
import values from '../../values/values';
import { applyStrokeWidthToPaths, applyWorkBorderToPaths, countGroupedHistoryLines, createInactiveTraceStyle, createMapValueLegends, DEFAULT_TRACE_OPACITY, getReportWorksData, groupHistoryByLine, isAlternatingLineConfiguration, isPlantioStatusIniciado, toCoordIdKey } from '../../usecases/mapPathUtils';
import { getWorkSeriesMeta } from '../../usecases/graphMultiWorkUtils';

const EARTH_RADIUS_M = 6378137;
const START_PASS_ANCHOR_DISTANCE_M = 50;
const START_HEADING_FALLBACK_POINT_COUNT = 3;
const SMOOTH_HEADING_WINDOW_M = 30;
const MIN_SEGMENT_METERS_FOR_HEADING = 2;
const STROKE_WIDTH_OVERLAP_FACTOR = 1.02;

const segmentDistanceMeters = (lat1, lng1, alt1, lat2, lng2, alt2) =>
    Number(values.distanceBetweenpointsM(lat1, lng1, alt1, lat2, lng2, alt2)) || 0;

const bearingDegrees = (lat1, lng1, lat2, lng2) => {
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaLng = ((lng2 - lng1) * Math.PI) / 180;
    const y = Math.sin(deltaLng) * Math.cos(phi2);
    const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLng);
    const theta = Math.atan2(y, x);
    return ((theta * 180) / Math.PI + 360) % 360;
};

const offsetLatLng = (lat, lng, bearingDeg, distanceMeters) => {
    if (!distanceMeters) {
        return { lat, lng };
    }

    const bearingRad = (bearingDeg * Math.PI) / 180;
    const latRad = (lat * Math.PI) / 180;
    const lngRad = (lng * Math.PI) / 180;
    const angularDistance = distanceMeters / EARTH_RADIUS_M;

    const newLatRad = Math.asin(
        Math.sin(latRad) * Math.cos(angularDistance) +
        Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
    );
    const newLngRad =
        lngRad +
        Math.atan2(
            Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
            Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(newLatRad)
        );

    return {
        lat: (newLatRad * 180) / Math.PI,
        lng: (newLngRad * 180) / Math.PI,
    };
};

const getLocalHeading = (trackPoints, index) => {
    if (!trackPoints?.length) {
        return null;
    }

    let lat1;
    let lng1;
    let lat2;
    let lng2;

    if (index > 0 && index < trackPoints.length - 1) {
        lat1 = trackPoints[index - 1].lat;
        lng1 = trackPoints[index - 1].lng;
        lat2 = trackPoints[index + 1].lat;
        lng2 = trackPoints[index + 1].lng;
    } else if (index > 0) {
        lat1 = trackPoints[index - 1].lat;
        lng1 = trackPoints[index - 1].lng;
        lat2 = trackPoints[index].lat;
        lng2 = trackPoints[index].lng;
    } else if (index < trackPoints.length - 1) {
        lat1 = trackPoints[index].lat;
        lng1 = trackPoints[index].lng;
        lat2 = trackPoints[index + 1].lat;
        lng2 = trackPoints[index + 1].lng;
    } else {
        return null;
    }

    return bearingDegrees(lat1, lng1, lat2, lng2);
};

const buildSmoothedHeadingByIndex = (trackPoints) => {
    if (!trackPoints?.length) {
        return [];
    }

    const cumulativeDistance = buildDistanceFromStartByIndex(trackPoints);
    const halfWindow = SMOOTH_HEADING_WINDOW_M / 2;
    const headings = new Array(trackPoints.length);

    for (let i = 0; i < trackPoints.length; i++) {
        const centerDistance = cumulativeDistance[i];
        const segmentHeadings = [];

        for (let j = 1; j < trackPoints.length; j++) {
            const segmentMid = (cumulativeDistance[j - 1] + cumulativeDistance[j]) / 2;

            if (Math.abs(segmentMid - centerDistance) > halfWindow) {
                continue;
            }

            const prev = trackPoints[j - 1];
            const curr = trackPoints[j];
            const segmentLength = segmentDistanceMeters(
                prev.lat,
                prev.lng,
                prev.alt,
                curr.lat,
                curr.lng,
                curr.alt
            );

            if (segmentLength < MIN_SEGMENT_METERS_FOR_HEADING) {
                continue;
            }

            segmentHeadings.push(bearingDegrees(prev.lat, prev.lng, curr.lat, curr.lng));
        }

        headings[i] =
            averageCircularHeading(segmentHeadings) ?? getLocalHeading(trackPoints, i);
    }

    return headings;
};

const buildDistanceFromStartByIndex = (trackPoints) => {
    if (!trackPoints?.length) {
        return [];
    }

    const distances = [0];
    for (let i = 1; i < trackPoints.length; i++) {
        const prev = trackPoints[i - 1];
        const curr = trackPoints[i];
        const segmentDistance = segmentDistanceMeters(
            prev.lat,
            prev.lng,
            prev.alt,
            curr.lat,
            curr.lng,
            curr.alt
        );
        distances.push(distances[i - 1] + segmentDistance);
    }

    return distances;
};

const buildDistanceFromPlantingStartByIndex = (distanceFromTrackStartByIndex, plantingStartIndex) => {
    if (!distanceFromTrackStartByIndex?.length) {
        return [];
    }

    const anchor =
        plantingStartIndex != null ? Number(distanceFromTrackStartByIndex[plantingStartIndex] ?? 0) : 0;

    return distanceFromTrackStartByIndex.map((distance, index) => {
        if (plantingStartIndex != null && index < plantingStartIndex) {
            return null;
        }
        return Math.max(0, Number(distance) - anchor);
    });
};

const averageCircularHeading = (headings) => {
    const valid = headings.filter((heading) => heading != null);
    if (!valid.length) {
        return null;
    }

    let sumX = 0;
    let sumY = 0;
    valid.forEach((heading) => {
        const rad = (heading * Math.PI) / 180;
        sumX += Math.cos(rad);
        sumY += Math.sin(rad);
    });

    return ((Math.atan2(sumY, sumX) * 180) / Math.PI + 360) % 360;
};

const computeStartPassHeading = (trackPoints) => {
    if (!trackPoints?.length) {
        return null;
    }

    const MIN_SEGMENT_METERS = 2;
    const segmentHeadings = [];
    let accumulatedDistance = 0;

    for (let i = 1; i < trackPoints.length && accumulatedDistance < START_PASS_ANCHOR_DISTANCE_M; i++) {
        const prev = trackPoints[i - 1];
        const curr = trackPoints[i];
        if (prev.lat == null || prev.lng == null || curr.lat == null || curr.lng == null) {
            continue;
        }

        const segmentDistance = segmentDistanceMeters(
            prev.lat,
            prev.lng,
            prev.alt,
            curr.lat,
            curr.lng,
            curr.alt
        );

        if (segmentDistance < MIN_SEGMENT_METERS) {
            continue;
        }

        segmentHeadings.push(bearingDegrees(prev.lat, prev.lng, curr.lat, curr.lng));
        accumulatedDistance += segmentDistance;
    }

    if (segmentHeadings.length) {
        return averageCircularHeading(segmentHeadings);
    }

    const localHeadings = [];
    const pointCount = Math.min(START_HEADING_FALLBACK_POINT_COUNT, trackPoints.length);
    for (let i = 0; i < pointCount; i++) {
        const heading = getLocalHeading(trackPoints, i);
        if (heading != null) {
            localHeadings.push(heading);
        }
    }

    return averageCircularHeading(localHeadings);
};

const computeDominantRowBearing = (trackPoints) => {
    if (!trackPoints || trackPoints.length < 2) {
        return null;
    }

    const MIN_SEGMENT_METERS = 2;
    let sumX = 0;
    let sumY = 0;
    let segmentCount = 0;

    for (let i = 1; i < trackPoints.length; i++) {
        const prev = trackPoints[i - 1];
        const curr = trackPoints[i];
        if (prev.lat == null || prev.lng == null || curr.lat == null || curr.lng == null) {
            continue;
        }

        const distance = segmentDistanceMeters(
            prev.lat,
            prev.lng,
            prev.alt,
            curr.lat,
            curr.lng,
            curr.alt
        );

        if (distance < MIN_SEGMENT_METERS) {
            continue;
        }

        const bearingRad = (bearingDegrees(prev.lat, prev.lng, curr.lat, curr.lng) * Math.PI) / 180;
        sumX += Math.cos(2 * bearingRad);
        sumY += Math.sin(2 * bearingRad);
        segmentCount++;
    }

    if (!segmentCount) {
        return null;
    }

    const dominantRad = Math.atan2(sumY, sumX) / 2;
    return ((dominantRad * 180) / Math.PI + 360) % 360;
};

const isHeadingAmbiguousForRow = (heading, rowBearing) => {
    const diff = (heading - rowBearing + 360) % 360;
    return (diff >= 75 && diff <= 105) || (diff >= 255 && diff <= 285);
};

const anchorRowBearingToStart = (trackPoints, rowBearing) => {
    if (rowBearing == null) {
        return null;
    }

    const startPassHeading = computeStartPassHeading(trackPoints);
    if (startPassHeading == null || isHeadingAmbiguousForRow(startPassHeading, rowBearing)) {
        return rowBearing;
    }

    const diff = (startPassHeading - rowBearing + 360) % 360;
    if (diff > 90 && diff < 270) {
        return (rowBearing + 180) % 360;
    }

    return rowBearing;
};

const resolveRowBearing = (trackPoints) => {
    const dominantRowBearing = computeDominantRowBearing(trackPoints);
    return anchorRowBearingToStart(trackPoints, dominantRowBearing);
};

const resolveHeadingForOffset = (
    trackPoints,
    pointIndex,
    rowBearing,
    distanceFromPlantingStart,
    smoothedHeadingByIndex = null
) => {
    if (distanceFromPlantingStart != null) {
        return (
            smoothedHeadingByIndex?.[pointIndex] ?? getLocalHeading(trackPoints, pointIndex)
        );
    }

    return rowBearing ?? getLocalHeading(trackPoints, pointIndex);
};

const resolveOffsetSign = (distanceFromPlantingStart, passOffsetSign = 1) => {
    if (distanceFromPlantingStart != null) {
        return 1;
    }

    return passOffsetSign;
};

const buildOffsetSignByPass = (trackPoints) => new Array(trackPoints?.length || 0).fill(1);

const buildReferenceTrack = (histLinhas, coords) => {
    if (Array.isArray(coords) && coords.length > 0) {
        return coords.map((coord) => ({
            lat: coord.lat,
            lng: coord.lng,
            alt: coord.alt,
            c_id: coord.id,
        }));
    }

    const byCId = {};
    histLinhas.forEach((item) => {
        if (item.c_id != null && item.c_lat != null && item.c_lng != null) {
            byCId[item.c_id] = {
                lat: item.c_lat,
                lng: item.c_lng,
                alt: item.c_alt,
                c_id: item.c_id,
            };
        }
    });

    return Object.values(byCId).sort((a, b) => a.c_id - b.c_id);
};

const resolveLineSpacing = (spacingMeters, totalWidthMeters, lineCount) => {
    if (spacingMeters && spacingMeters > 0) {
        return spacingMeters;
    }
    if (totalWidthMeters && lineCount > 0) {
        return totalWidthMeters / lineCount;
    }
    return 0;
};

const resolvePlantingLineStrokeWidthMeters = (lineSpacingMeters, isAlternatingLines) => {
    if (!lineSpacingMeters || lineSpacingMeters <= 0) {
        return 0;
    }

    const strokeMultiplier = isAlternatingLines ? 2 : 1;
    return lineSpacingMeters * strokeMultiplier * STROKE_WIDTH_OVERLAP_FACTOR;
};

const computeLineLateralOffset = (lineNumber, lineCount, spacingMeters, totalWidthMeters) => {
    const spacing = resolveLineSpacing(spacingMeters, totalWidthMeters, lineCount);
    if (!spacing || lineNumber == null) {
        return 0;
    }
    return (lineNumber - (lineCount + 1) / 2) * spacing;
};

const offsetPointPerpendicular = (
    lat,
    lng,
    trackPoints,
    pointIndex,
    lateralOffsetMeters,
    offsetSign = 1,
    headingForOffset = null
) => {
    if (!lateralOffsetMeters) {
        return { lat, lng };
    }

    const trackHeading =
        headingForOffset != null
            ? headingForOffset
            : getLocalHeading(trackPoints, pointIndex);
    const perpendicularBearing = trackHeading != null ? (trackHeading + 90) % 360 : 90;
    const adjustedOffset = lateralOffsetMeters * offsetSign;

    return offsetLatLng(lat, lng, perpendicularBearing, adjustedOffset);
};

const findLegendForValue = (legends, value) => {
    return legends.find((legend) => {
        if (!legend.show) return false;
        if (legend.max != null && legend.min != null) {
            return value < legend.max && value >= legend.min;
        }
        if (legend.max != null && legend.min == null) {
            return value < legend.max;
        }
        if (legend.max == null && legend.min != null) {
            return value >= legend.min;
        }
        return false;
    });
};

const hasPlantingSmp = (smp) => smp != null && smp > 0;

const sameCoordId = (a, b) => {
    const keyA = toCoordIdKey(a);
    const keyB = toCoordIdKey(b);
    return keyA != null && keyB != null && keyA === keyB;
};

const coordIdLessThan = (a, b) => {
    const keyA = toCoordIdKey(a);
    const keyB = toCoordIdKey(b);
    return keyA != null && keyB != null && keyA < keyB;
};

const buildSortedHistPlantio = (histPlantio) => {
    if (!Array.isArray(histPlantio) || histPlantio.length === 0) {
        return [];
    }
    return [...histPlantio].sort((a, b) => a.c_id - b.c_id || a.id - b.id);
};

const PLANTING_PATH_OPACITY = DEFAULT_TRACE_OPACITY;
const MAX_PATH_SEGMENT_GAP_METERS = 20;

const isUsaSementesMetroSegmento = (value) => value === true || value === 1;

const distanceBetweenEnrichedPoints = (fromPoint, toPoint) => {
    if (!fromPoint || !toPoint) {
        return 0;
    }
    return segmentDistanceMeters(
        fromPoint.c_lat,
        fromPoint.c_lng,
        fromPoint.c_alt,
        toPoint.c_lat,
        toPoint.c_lng,
        toPoint.c_alt
    );
};

const resolveLegendForSmp = (legends, smp) => {
    for (let i = 0; i < legends.length; i++) {
        const legend = legends[i];
        if (findLegendForValue([legend], smp)) {
            return legend;
        }
    }
    return null;
};

const pushPlantingPathPoint = ({
    paths,
    curpoint,
    legend,
    lastAnchor,
    enrichedPoint,
    forceNewPath,
}) => {
    if (!legend?.show) {
        return;
    }

    const distance = distanceBetweenEnrichedPoints(lastAnchor, enrichedPoint);
    const lastSegment = paths[paths.length - 1];
    let needCreatePath =
        forceNewPath || paths.length === 0 || distance > MAX_PATH_SEGMENT_GAP_METERS;

    if (
        lastSegment?.segmentType === 'planting' &&
        lastSegment.legendId != null &&
        lastSegment.legendId !== legend.id
    ) {
        needCreatePath = true;
    }

    if (needCreatePath) {
        if (
            lastSegment?.segmentType === 'planting' &&
            distance <= MAX_PATH_SEGMENT_GAP_METERS &&
            !forceNewPath
        ) {
            lastSegment.path.push(curpoint);
        }
        paths.push({
            path: [curpoint],
            color: legend.color,
            opacity: PLANTING_PATH_OPACITY,
            segmentType: 'planting',
            legendId: legend.id,
        });
    } else if (lastSegment?.segmentType === 'planting') {
        lastSegment.path.push(curpoint);
    }
};

const pushInactiveTrackPoint = (paths, curpoint, lastAnchor, enrichedPoint, forceNewPath) => {
    const distance = distanceBetweenEnrichedPoints(lastAnchor, enrichedPoint);
    const lastSegment = paths[paths.length - 1];
    const needCreatePath =
        forceNewPath ||
        paths.length === 0 ||
        !lastSegment?.thinLine ||
        distance > MAX_PATH_SEGMENT_GAP_METERS;

    if (needCreatePath) {
        if (lastSegment?.thinLine && distance <= MAX_PATH_SEGMENT_GAP_METERS && !forceNewPath) {
            lastSegment.path.push(curpoint);
        }
        paths.push({
            path: [curpoint],
            ...createInactiveTraceStyle(),
        });
    } else if (lastSegment?.thinLine) {
        lastSegment.path.push(curpoint);
    }
};

/**
 * Traçado cinza fino no eixo do GPS enquanto o plantio não está INICIADO.
 * Separado das linhas coloridas por linha do implemento.
 */
const buildInactivePlantioTrackFromCoords = (coords, sortedHistPlantio) => {
    if (!Array.isArray(coords) || coords.length === 0 || !sortedHistPlantio?.length) {
        return [];
    }

    const paths = [];
    let lastPlantioIndex = 0;
    let activePlantioStatus = null;
    let lastAnchor = null;
    let forceNewPath = false;
    let lastWasInactive = null;

    for (let c = 0; c < coords.length; c++) {
        const coord = coords[c];

        for (let p = lastPlantioIndex; p < sortedHistPlantio.length; p++) {
            if (sameCoordId(coord.id, sortedHistPlantio[p].c_id)) {
                activePlantioStatus = sortedHistPlantio[p].stat;
                lastPlantioIndex = p;
            } else if (coordIdLessThan(coord.id, sortedHistPlantio[p].c_id)) {
                break;
            }
        }

        const isInactive = !isPlantioStatusIniciado(activePlantioStatus);
        const enrichedPoint = {
            c_lat: coord.lat,
            c_lng: coord.lng,
            c_alt: coord.alt,
        };
        const curpoint = { lat: coord.lat, lng: coord.lng };

        if (!isInactive) {
            if (lastWasInactive) {
                forceNewPath = true;
            }
            lastWasInactive = false;
            lastAnchor = enrichedPoint;
            continue;
        }

        if (lastWasInactive === false) {
            forceNewPath = true;
        }

        pushInactiveTrackPoint(paths, curpoint, lastAnchor, enrichedPoint, forceNewPath);
        forceNewPath = false;
        lastWasInactive = true;
        lastAnchor = enrichedPoint;
    }

    return paths.filter((segment) => segment.path && segment.path.length >= 2);
};

const buildInactivePlantioTrackFromPlantioHistory = (sortedHistPlantio) => {
    if (!sortedHistPlantio?.length) {
        return [];
    }

    const paths = [];
    let lastAnchor = null;
    let forceNewPath = false;
    let lastWasInactive = null;

    for (let i = 0; i < sortedHistPlantio.length; i++) {
        const item = sortedHistPlantio[i];
        if (item?.c_lat == null || item?.c_lng == null) {
            continue;
        }

        const isInactive = !isPlantioStatusIniciado(item.stat);
        const enrichedPoint = {
            c_lat: item.c_lat,
            c_lng: item.c_lng,
            c_alt: item.c_alt,
        };
        const curpoint = {
            lat: parseFloat(item.c_lat),
            lng: parseFloat(item.c_lng),
        };

        if (!isInactive) {
            if (lastWasInactive) {
                forceNewPath = true;
            }
            lastWasInactive = false;
            lastAnchor = enrichedPoint;
            continue;
        }

        if (lastWasInactive === false) {
            forceNewPath = true;
        }

        pushInactiveTrackPoint(paths, curpoint, lastAnchor, enrichedPoint, forceNewPath);
        forceNewPath = false;
        lastWasInactive = true;
        lastAnchor = enrichedPoint;
    }

    return paths.filter((segment) => segment.path && segment.path.length >= 2);
};

const resolveHistLinhasSmpForMap = (histLinhas, useSegmentMode) => {
    if (!useSegmentMode || !Array.isArray(histLinhas)) {
        return histLinhas;
    }
    return histLinhas.map((item) => ({
        ...item,
        smp: item.smp_segmento != null ? Number(item.smp_segmento) : 0,
    }));
};

const buildPathsForLineAndCoords = (
    lineHistory,
    coords,
    legends,
    lateralOffsetMeters = 0,
    offsetSignByIndex = null,
    rowBearing = null,
    distanceFromPlantingStartByIndex = null,
    trackPoints = null,
    smoothedHeadingByIndex = null,
    useSegmentMode = false,
    sortedHistPlantio = null,
    usePlantioStatusGate = false
) => {
    if (!lineHistory?.length || !coords?.length || !legends?.length) {
        return [];
    }

    const resolvedTrackPoints =
        trackPoints ??
        coords.map((coord) => ({
            lat: coord.lat,
            lng: coord.lng,
            alt: coord.alt,
        }));
    const resolvedSmoothedHeadingByIndex =
        smoothedHeadingByIndex ?? buildSmoothedHeadingByIndex(resolvedTrackPoints);
    const sortedHistory = [...lineHistory].sort((a, b) => a.c_id - b.c_id || a.id - b.id);
    let lastPointIndex = 0;
    let lastpoint = null;
    let lastAnchor = null;
    let curLegend = null;
    let activeSegmentSmp = null;
    let inGap = false;
    let activePlantioStatus = null;
    let inPlantioGap = false;
    let lastPlantioIndex = 0;
    const paths = [];

    for (let c = 0; c < coords.length; c++) {
        const coord = coords[c];
        let histSnapshot = null;
        let matchedHist = false;

        for (let i = lastPointIndex; i < sortedHistory.length; i++) {
            if (sameCoordId(coord.id, sortedHistory[i].c_id)) {
                histSnapshot = sortedHistory[i];
                lastPointIndex = i;
                matchedHist = true;
            } else if (coordIdLessThan(coord.id, sortedHistory[i].c_id)) {
                break;
            }
        }

        if (usePlantioStatusGate && sortedHistPlantio?.length) {
            for (let p = lastPlantioIndex; p < sortedHistPlantio.length; p++) {
                if (sameCoordId(coord.id, sortedHistPlantio[p].c_id)) {
                    activePlantioStatus = sortedHistPlantio[p].stat;
                    lastPlantioIndex = p;
                } else if (coordIdLessThan(coord.id, sortedHistPlantio[p].c_id)) {
                    break;
                }
            }

            if (!isPlantioStatusIniciado(activePlantioStatus)) {
                activeSegmentSmp = null;
                inGap = true;
                inPlantioGap = true;
                curLegend = null;
                lastpoint = null;
                lastAnchor = {
                    c_lat: coord.lat,
                    c_lng: coord.lng,
                    c_alt: coord.alt,
                };
                continue;
            }

            if (inPlantioGap) {
                inGap = true;
                inPlantioGap = false;
            }
        }

        if (useSegmentMode) {
            if (matchedHist) {
                if (!hasPlantingSmp(histSnapshot.smp)) {
                    activeSegmentSmp = null;
                    inGap = true;
                    curLegend = null;
                    lastpoint = null;
                    lastAnchor = {
                        c_lat: coord.lat,
                        c_lng: coord.lng,
                        c_alt: coord.alt,
                    };
                    continue;
                }
                activeSegmentSmp = histSnapshot.smp;
            } else if (!hasPlantingSmp(activeSegmentSmp)) {
                inGap = true;
                lastAnchor = {
                    c_lat: coord.lat,
                    c_lng: coord.lng,
                    c_alt: coord.alt,
                };
                continue;
            }

            const enrichedPoint = {
                ...(matchedHist ? histSnapshot : {}),
                smp: matchedHist ? histSnapshot.smp : activeSegmentSmp,
                c_lat: coord.lat,
                c_lng: coord.lng,
                c_alt: coord.alt,
            };

            if (!hasPlantingSmp(enrichedPoint.smp)) {
                activeSegmentSmp = null;
                inGap = true;
                curLegend = null;
                lastpoint = null;
                lastAnchor = enrichedPoint;
                continue;
            }

            if (!lastpoint || !hasPlantingSmp(lastpoint.smp)) {
                lastpoint = enrichedPoint;
            }

            const distanceFromPlantingStart = distanceFromPlantingStartByIndex?.[c];
            const headingForOffset = resolveHeadingForOffset(
                resolvedTrackPoints,
                c,
                rowBearing,
                distanceFromPlantingStart,
                resolvedSmoothedHeadingByIndex
            );
            const offsetSign = resolveOffsetSign(
                distanceFromPlantingStart,
                offsetSignByIndex?.[c] ?? 1
            );
            const curpoint = offsetPointPerpendicular(
                enrichedPoint.c_lat,
                enrichedPoint.c_lng,
                resolvedTrackPoints,
                c,
                lateralOffsetMeters,
                offsetSign,
                headingForOffset
            );
            const legend = resolveLegendForSmp(legends, enrichedPoint.smp);
            const forceNewPath = inGap;
            inGap = false;

            pushPlantingPathPoint({
                paths,
                curpoint,
                legend,
                lastAnchor,
                enrichedPoint,
                forceNewPath,
            });

            if (legend) {
                curLegend = legend;
            }
            lastpoint = enrichedPoint;
            lastAnchor = enrichedPoint;
            continue;
        }

        if (matchedHist) {
            if (!hasPlantingSmp(histSnapshot.smp)) {
                curLegend = null;
                lastpoint = null;
                continue;
            }
        } else {
            histSnapshot = lastpoint;
            if (!histSnapshot) {
                continue;
            }
        }

        const enrichedPoint = {
            ...(matchedHist ? histSnapshot : {}),
            smp: matchedHist ? histSnapshot.smp : histSnapshot.smp,
            c_lat: coord.lat,
            c_lng: coord.lng,
            c_alt: coord.alt,
        };

        if (!hasPlantingSmp(enrichedPoint.smp)) {
            curLegend = null;
            lastpoint = null;
            continue;
        }

        if (!lastpoint || !hasPlantingSmp(lastpoint.smp)) {
            lastpoint = enrichedPoint;
        }

        const distance = segmentDistanceMeters(
            lastpoint.c_lat,
            lastpoint.c_lng,
            lastpoint.c_alt,
            enrichedPoint.c_lat,
            enrichedPoint.c_lng,
            enrichedPoint.c_alt
        );

        const distanceFromPlantingStart = distanceFromPlantingStartByIndex?.[c];
        const headingForOffset = resolveHeadingForOffset(
            resolvedTrackPoints,
            c,
            rowBearing,
            distanceFromPlantingStart,
            resolvedSmoothedHeadingByIndex
        );
        const offsetSign = resolveOffsetSign(
            distanceFromPlantingStart,
            offsetSignByIndex?.[c] ?? 1
        );
        const curpoint = offsetPointPerpendicular(
            enrichedPoint.c_lat,
            enrichedPoint.c_lng,
            resolvedTrackPoints,
            c,
            lateralOffsetMeters,
            offsetSign,
            headingForOffset
        );
        let needCreatePath = paths.length === 0;

        if (distance > MAX_PATH_SEGMENT_GAP_METERS) {
            needCreatePath = true;
        }

        for (let l = 0; l < legends.length; l++) {
            const legend = legends[l];
            if (findLegendForValue([legend], enrichedPoint.smp)) {
                if (!curLegend) {
                    curLegend = legend;
                } else if (curLegend.id !== legend.id) {
                    needCreatePath = true;
                }
                curLegend = legend;
                break;
            }
        }

        if (needCreatePath) {
            if (paths.length > 0 && distance <= MAX_PATH_SEGMENT_GAP_METERS) {
                paths[paths.length - 1].path.push(curpoint);
            }
            if (curLegend) {
                paths.push({
                    path: [curpoint],
                    color: curLegend.color,
                    opacity: PLANTING_PATH_OPACITY,
                    segmentType: 'planting',
                    legendId: curLegend.id,
                });
            }
        } else if (curLegend && curLegend.show && paths.length > 0) {
            paths[paths.length - 1].path.push(curpoint);
        }

        lastpoint = enrichedPoint;
    }

    return paths.filter((segment) => segment.path && segment.path.length >= 2);
};

const buildPathsForHistoryDirect = (
    currentHistory,
    legends,
    lateralOffsetMeters = 0,
    offsetSignByCId = null,
    rowBearing = null,
    distanceFromPlantingStartByCId = null,
    useSegmentMode = false,
    plantioStatusByCId = null,
    usePlantioStatusGate = false
) => {
    if (!currentHistory?.length || !legends?.length) {
        return [];
    }

    const sortedHistory = [...currentHistory].sort(
        (a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id
    );
    const trackPoints = sortedHistory.map((item) => ({
        lat: item.c_lat,
        lng: item.c_lng,
        alt: item.c_alt,
    }));
    const smoothedHeadingByIndex = buildSmoothedHeadingByIndex(trackPoints);

    let lastpoint = null;
    let lastAnchor = null;
    let curLegend = null;
    let inGap = false;
    let activePlantioStatus = null;
    let inPlantioGap = false;
    const paths = [];

    sortedHistory.forEach((item, index) => {
        const enrichedPoint = {
            ...item,
            c_lat: item.c_lat,
            c_lng: item.c_lng,
            c_alt: item.c_alt,
        };

        if (usePlantioStatusGate && plantioStatusByCId && item.c_id != null) {
            const statusKey = toCoordIdKey(item.c_id);
            if (statusKey != null && Object.prototype.hasOwnProperty.call(plantioStatusByCId, statusKey)) {
                activePlantioStatus = plantioStatusByCId[statusKey];
            }

            if (!isPlantioStatusIniciado(activePlantioStatus)) {
                inGap = true;
                inPlantioGap = true;
                curLegend = null;
                lastAnchor = enrichedPoint;
                return;
            }

            if (inPlantioGap) {
                inGap = true;
                inPlantioGap = false;
            }
        }

        if (useSegmentMode && !hasPlantingSmp(enrichedPoint.smp)) {
            inGap = true;
            curLegend = null;
            lastAnchor = enrichedPoint;
            return;
        }

        if (!hasPlantingSmp(enrichedPoint.smp)) {
            curLegend = null;
            lastpoint = null;
            return;
        }

        if (!lastpoint || !hasPlantingSmp(lastpoint.smp)) {
            lastpoint = enrichedPoint;
        }

        const headingForOffset = resolveHeadingForOffset(
            trackPoints,
            index,
            rowBearing,
            distanceFromPlantingStartByCId?.[item.c_id],
            smoothedHeadingByIndex
        );
        const offsetSign = resolveOffsetSign(
            distanceFromPlantingStartByCId?.[item.c_id],
            offsetSignByCId?.[item.c_id] ?? 1
        );
        const curpoint = offsetPointPerpendicular(
            enrichedPoint.c_lat,
            enrichedPoint.c_lng,
            trackPoints,
            index,
            lateralOffsetMeters,
            offsetSign,
            headingForOffset
        );

        if (useSegmentMode) {
            const legend = resolveLegendForSmp(legends, enrichedPoint.smp);
            const forceNewPath = inGap;
            inGap = false;

            pushPlantingPathPoint({
                paths,
                curpoint,
                legend,
                lastAnchor,
                enrichedPoint,
                forceNewPath,
            });

            if (legend) {
                curLegend = legend;
            }
            lastpoint = enrichedPoint;
            lastAnchor = enrichedPoint;
            return;
        }

        const distance = segmentDistanceMeters(
            lastpoint.c_lat,
            lastpoint.c_lng,
            lastpoint.c_alt,
            enrichedPoint.c_lat,
            enrichedPoint.c_lng,
            enrichedPoint.c_alt
        );

        let needCreatePath = paths.length === 0;

        if (distance > MAX_PATH_SEGMENT_GAP_METERS) {
            needCreatePath = true;
        }

        for (let l = 0; l < legends.length; l++) {
            const legend = legends[l];
            if (findLegendForValue([legend], enrichedPoint.smp)) {
                if (!curLegend) {
                    curLegend = legend;
                } else if (curLegend.id !== legend.id) {
                    needCreatePath = true;
                }
                curLegend = legend;
                break;
            }
        }

        if (needCreatePath) {
            if (paths.length > 0 && distance <= MAX_PATH_SEGMENT_GAP_METERS) {
                paths[paths.length - 1].path.push(curpoint);
            }
            if (curLegend) {
                paths.push({
                    path: [curpoint],
                    color: curLegend.color,
                    opacity: PLANTING_PATH_OPACITY,
                    segmentType: 'planting',
                    legendId: curLegend.id,
                });
            }
        } else if (curLegend && curLegend.show && paths.length > 0) {
            paths[paths.length - 1].path.push(curpoint);
        }

        lastpoint = enrichedPoint;
    });

    return paths.filter((segment) => segment.path && segment.path.length >= 2);
};

const buildPlantioStatusByCId = (sortedHistPlantio) => {
    if (!sortedHistPlantio?.length) {
        return null;
    }

    return sortedHistPlantio.reduce((map, item) => {
        const key = toCoordIdKey(item.c_id);
        if (key != null) {
            map[key] = item.stat;
        }
        return map;
    }, {});
};

const buildAllLinePaths = (
    histLinhas,
    coords,
    legends,
    offsetConfig,
    useSegmentMode = false,
    sortedHistPlantio = null,
    usePlantioStatusGate = false
) => {
    const lineGroups = groupHistoryByLine(histLinhas);
    const useCoords = Array.isArray(coords) && coords.length > 0;
    const lineCount = offsetConfig?.lineCount || lineGroups.length || 1;
    const plantioStatusByCId = usePlantioStatusGate
        ? buildPlantioStatusByCId(sortedHistPlantio)
        : null;

    const referenceTrack = buildReferenceTrack(histLinhas, coords);
    const rowBearing = resolveRowBearing(referenceTrack);
    const { distanceFromPlantingStartByIndex } = resolvePlantingContext(histLinhas, coords, referenceTrack);
    const offsetSignByIndex = buildOffsetSignByPass(referenceTrack);
    const distanceFromPlantingStartByCId = referenceTrack.reduce((map, point, index) => {
        if (point.c_id != null) {
            map[point.c_id] = distanceFromPlantingStartByIndex[index];
        }
        return map;
    }, {});
    const offsetSignByCId = referenceTrack.reduce((map, point, index) => {
        if (point.c_id != null) {
            map[point.c_id] = offsetSignByIndex[index];
        }
        return map;
    }, {});

    const trackPoints = useCoords
        ? coords.map((coord) => ({
              lat: coord.lat,
              lng: coord.lng,
              alt: coord.alt,
          }))
        : null;
    const smoothedHeadingByIndex = trackPoints
        ? buildSmoothedHeadingByIndex(trackPoints)
        : null;

    return lineGroups.flatMap(({ lineNumber, history }) => {
        const lateralOffset = computeLineLateralOffset(
            lineNumber,
            lineCount,
            offsetConfig?.spacingMeters,
            offsetConfig?.totalWidthMeters
        );

        return useCoords
            ? buildPathsForLineAndCoords(
                  history,
                  coords,
                  legends,
                  lateralOffset,
                  offsetSignByIndex,
                  rowBearing,
                  distanceFromPlantingStartByIndex,
                  trackPoints,
                  smoothedHeadingByIndex,
                  useSegmentMode,
                  sortedHistPlantio,
                  usePlantioStatusGate
              )
            : buildPathsForHistoryDirect(
                  history,
                  legends,
                  lateralOffset,
                  offsetSignByCId,
                  rowBearing,
                  distanceFromPlantingStartByCId,
                  useSegmentMode,
                  plantioStatusByCId,
                  usePlantioStatusGate
              );
    }).concat(
        usePlantioStatusGate
            ? (useCoords
                ? buildInactivePlantioTrackFromCoords(coords, sortedHistPlantio)
                : buildInactivePlantioTrackFromPlantioHistory(sortedHistPlantio))
            : []
    );
};


const findFirstCoordIndexForCIds = (coords, cIds) => {
    if (!coords?.length || !cIds?.size) {
        return null;
    }
    for (let index = 0; index < coords.length; index++) {
        if (cIds.has(coords[index].id)) {
            return index;
        }
    }
    return null;
};

const resolvePlantingContext = (histLinhas, coords, referenceTrack) => {
    const histCIds = new Set(histLinhas.map((item) => item.c_id).filter((id) => id != null));
    const hasCoords = Array.isArray(coords) && coords.length > 0;
    const plantingStartIndex = hasCoords ? findFirstCoordIndexForCIds(coords, histCIds) : 0;

    const distanceFromTrackStartByIndex = buildDistanceFromStartByIndex(referenceTrack);
    const distanceFromPlantingStartByIndex = buildDistanceFromPlantingStartByIndex(
        distanceFromTrackStartByIndex,
        plantingStartIndex ?? 0
    );

    return {
        plantingStartIndex: plantingStartIndex ?? 0,
        distanceFromTrackStartByIndex,
        distanceFromPlantingStartByIndex,
    };
};


const Map_sementesmetro = ({ data, implementWidthMeters, lineWidthMeters, configuredLineCount, usaSementesMetroSegmento }) => {
    const [legends, setLegends] = useState([]);
    const [path, setPath] = useState([]);
    const [marker, setMarker] = useState([]);
    const [centerMap, setCenterMap] = useState(null);
    const [loadingMap, setLoadingMap] = useState(true);
    const [sliderValue, setSliderValue] = useState([0, 100]);
    const [realMin, setRealMin] = useState(0);
    const [realMax, setRealMax] = useState(100);
    const isInitialLoadRef = useRef(true);

    const histLinhas = useMemo(() => {
        const raw = data?.[0]?.histLinhas;
        return Array.isArray(raw) ? raw : [];
    }, [data]);

    const useSegmentMode = isUsaSementesMetroSegmento(usaSementesMetroSegmento);

    const histLinhasForMap = useMemo(
        () => resolveHistLinhasSmpForMap(histLinhas, useSegmentMode),
        [histLinhas, useSegmentMode]
    );

    const histCoords = useMemo(() => {
        const raw = data?.[3]?.histCoords;
        return Array.isArray(raw) ? raw : [];
    }, [data]);

    const histPlantio = useMemo(() => {
        const raw = data?.[2]?.histPlantio;
        return Array.isArray(raw) ? raw : [];
    }, [data]);

    const sortedHistPlantio = useMemo(
        () => buildSortedHistPlantio(histPlantio),
        [histPlantio]
    );

    const usePlantioStatusGate = sortedHistPlantio.length > 0;

    const histLinhasReady = data?.[0]?.histLinhasReady === true;
    const histCoordsReady = data?.[3]?.histCoords !== null && data?.[3]?.histCoords !== undefined;

    const lineCount = useMemo(() => {
        if (configuredLineCount && configuredLineCount > 0) {
            return configuredLineCount;
        }
        const numeros = histLinhas.map((item) => item.numero).filter((numero) => numero != null);
        return numeros.length ? Math.max(...numeros) : 1;
    }, [histLinhas, configuredLineCount]);

    const groupedLineCount = useMemo(() => countGroupedHistoryLines(histLinhas), [histLinhas]);

    const isAlternatingLines = useMemo(
        () => isAlternatingLineConfiguration(groupedLineCount, lineCount),
        [groupedLineCount, lineCount]
    );

    const baseLineSpacingMeters = useMemo(
        () => resolveLineSpacing(lineWidthMeters, implementWidthMeters, lineCount),
        [lineWidthMeters, implementWidthMeters, lineCount]
    );

    const lineSpacingMeters = useMemo(() => {
        return (
            baseLineSpacingMeters ||
            (implementWidthMeters && lineCount > 0 ? implementWidthMeters / lineCount : 0) ||
            lineWidthMeters ||
            0
        );
    }, [baseLineSpacingMeters, lineWidthMeters, implementWidthMeters, lineCount]);

    const plantingLineStrokeWidthMeters = useMemo(
        () => resolvePlantingLineStrokeWidthMeters(lineSpacingMeters, isAlternatingLines),
        [lineSpacingMeters, isAlternatingLines]
    );

    const offsetConfig = useMemo(
        () => ({
            lineCount,
            spacingMeters: lineSpacingMeters || undefined,
            totalWidthMeters: implementWidthMeters,
            alternatingLines: isAlternatingLines,
            groupedLineCount,
        }),
        [lineCount, lineSpacingMeters, implementWidthMeters, isAlternatingLines, groupedLineCount]
    );

    const createLegends = (newMin, newMax) => {
        const newLegends = createMapValueLegends(newMin, newMax);
        setLegends(newLegends);
        return newLegends;
    };

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const applyLimits = () => {
        const [newMin, newMax] = sliderValue;
        createLegends(newMin, newMax);
    };

    const buildAndLogPaths = (legendList) => {
        const works = getReportWorksData(data);
        const showBorders = works.length > 1;
        const allPaths = [];

        works.forEach((workData, index) => {
            const workHistLinhas = Array.isArray(workData?.[0]?.histLinhas) ? workData[0].histLinhas : [];
            const workMapConfig = workData?.mapConfig || {};
            const workUsaSegmento =
                workMapConfig.usaSementesMetroSegmento ?? usaSementesMetroSegmento;
            const workUseSegmentMode = isUsaSementesMetroSegmento(workUsaSegmento);
            const workHistLinhasForMap = resolveHistLinhasSmpForMap(workHistLinhas, workUseSegmentMode);
            if (!workHistLinhasForMap.length) {
                return;
            }

            const workHistCoords = Array.isArray(workData?.[3]?.histCoords) ? workData[3].histCoords : [];
            const workHistPlantio = Array.isArray(workData?.[2]?.histPlantio) ? workData[2].histPlantio : [];
            const workSortedHistPlantio = buildSortedHistPlantio(workHistPlantio);
            const workUsePlantioStatusGate = workSortedHistPlantio.length > 0;

            const workConfiguredLineCount =
                workMapConfig.configuredLineCount ?? configuredLineCount;
            const workLineWidthMeters = workMapConfig.lineWidthMeters ?? lineWidthMeters;
            const workImplementWidthMeters =
                workMapConfig.implementWidthMeters ?? implementWidthMeters;

            const workLineCount =
                workConfiguredLineCount && workConfiguredLineCount > 0
                    ? workConfiguredLineCount
                    : (() => {
                          const numeros = workHistLinhas
                              .map((item) => item.numero)
                              .filter((numero) => numero != null);
                          return numeros.length ? Math.max(...numeros) : lineCount;
                      })();

            const workGroupedLineCount = countGroupedHistoryLines(workHistLinhas);
            const workIsAlternating = isAlternatingLineConfiguration(
                workGroupedLineCount,
                workLineCount
            );
            const workBaseLineSpacing = resolveLineSpacing(
                workLineWidthMeters,
                workImplementWidthMeters,
                workLineCount
            );
            const workLineSpacingMeters =
                workBaseLineSpacing ||
                (workImplementWidthMeters && workLineCount > 0
                    ? workImplementWidthMeters / workLineCount
                    : 0) ||
                workLineWidthMeters ||
                0;
            const workStrokeWidthMeters = resolvePlantingLineStrokeWidthMeters(
                workLineSpacingMeters,
                workIsAlternating
            );

            const workOffsetConfig = {
                lineCount: workLineCount,
                spacingMeters: workLineSpacingMeters || undefined,
                totalWidthMeters: workImplementWidthMeters,
                alternatingLines: workIsAlternating,
                groupedLineCount: workGroupedLineCount,
            };

            const paths = buildAllLinePaths(
                workHistLinhasForMap,
                workHistCoords,
                legendList,
                workOffsetConfig,
                workUseSegmentMode,
                workSortedHistPlantio,
                workUsePlantioStatusGate
            );

            applyStrokeWidthToPaths(
                paths,
                workStrokeWidthMeters || workLineSpacingMeters || workLineWidthMeters || 1
            );

            if (showBorders) {
                applyWorkBorderToPaths(paths, getWorkSeriesMeta(workData, index));
            }

            allPaths.push(...paths);
        });

        return allPaths;
    };

    const processPath = () => {
        if (!histLinhasForMap.length || !legends.length) {
            return;
        }

        const hasActiveLegend = legends.some((legend) => legend.show);
        if (!hasActiveLegend) {
            setPath([]);
            return;
        }

        setPath(buildAndLogPaths(legends));
    };

    const handleLegendChange = (legendId) => {
        const legendsCopy = legends.map((legend) =>
            legend.id === legendId ? { ...legend, show: !legend.show } : legend
        );
        setLegends(legendsCopy);
    };

    useEffect(() => {
        isInitialLoadRef.current = true;

        if (!histLinhasReady || !histCoordsReady) {
            setLoadingMap(true);
            return;
        }

        if (histLinhasForMap.length > 0) {
            const min = histLinhasForMap.reduce(
                (acc, item) => item.smp > 0 && item.smp < acc ? item.smp : acc,
                Number.MAX_VALUE
            );
            const max = histLinhasForMap.reduce(
                (acc, item) => item.smp > acc ? item.smp : acc,
                0
            );

            const newRealMin = min !== Number.MAX_VALUE ? min : 0;
            const newRealMax = max;

            setRealMin(newRealMin);
            setRealMax(newRealMax);
            setSliderValue([newRealMin, newRealMax]);

            const newLegends = createLegends(newRealMin, newRealMax);
            isInitialLoadRef.current = false;

            const paths = buildAndLogPaths(newLegends);
            setPath(paths);
            setLoadingMap(false);

            const routeCoords = histCoords.length > 0
                ? histCoords
                : [...histLinhasForMap].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);

            const first = routeCoords[0];
            const last = routeCoords[routeCoords.length - 1];
            const firstLat = first.lat ?? first.c_lat;
            const firstLng = first.lng ?? first.c_lng;
            const lastLat = last.lat ?? last.c_lat;
            const lastLng = last.lng ?? last.c_lng;

            setMarker([
                { latitude: firstLat, longitude: firstLng, coordinateType: 'first' },
                { latitude: lastLat, longitude: lastLng, coordinateType: 'last' }
            ]);
            setCenterMap({ lat: firstLat, lng: firstLng });
        } else {
            setPath([]);
            setLoadingMap(false);
        }
    }, [data, histLinhasForMap, histCoords, histPlantio, sortedHistPlantio, usePlantioStatusGate, histLinhasReady, histCoordsReady, offsetConfig, useSegmentMode, configuredLineCount]);

    useEffect(() => {
        if (isInitialLoadRef.current) {
            return;
        }
        if (legends && legends.length > 0) {
            processPath();
        }
    }, [legends]);

    return (
        <div style={{width: '100%', alignItems:'center'}}>
            <h3>Variação de sementes por metro durante o trabalho</h3>
            {loadingMap ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <div className="spinner"></div>
                    <BlurCircularOutlined style={{ fontSize: '40px', marginLeft: '20px' }} />
                </div>
            ) : histLinhas.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhum histórico de linhas disponível para este trabalho.
                    </Typography>
                </div>
            ) : (
                <>
                    <MapPanel
                        markers={marker}
                        zoomMap={17}
                        centerMap={centerMap}
                        linhas={path}
                        height={600}
                        strokeWidthMeters={plantingLineStrokeWidthMeters || lineSpacingMeters || lineWidthMeters || 1}
                    />
                    <MapLegend legends={legends} onLegendChange={handleLegendChange} />
                    <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', margin: '20px 0', gap: 8, width: '100%' }}>
                        <Slider
                            style={{ width: 'min(60%, 100%)', flex: 1, minWidth: 160, marginRight: '20px', marginLeft: '24px' }}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            min={realMin}
                            max={realMax}
                        />
                        <Button
                            onClick={applyLimits}
                            style={{ backgroundColor: '#ffca20', color: '#000', borderRadius: '5px' }}
                        >
                            Aplicar Limites
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Map_sementesmetro;

// TODO: Adicionar informação se o trabalho usa linhas alternadas
